'use client';

import { useState, useRef, useEffect } from 'react';
import { useLocale } from 'next-intl';
import { Globe, Check } from 'lucide-react';

const PUBLIC_LANG_COOKIE = 'alix_public_lang';

type LocaleOption = {
  code: string;
  label: string;
};

const LOCALES: LocaleOption[] = [
  { code: 'en', label: 'English' },
  { code: 'vi', label: 'Tiếng Việt' },
];

interface LangSwitcherProps {
  className?: string;
  textClass?: string;
}

export function LangSwitcher({ className, textClass }: LangSwitcherProps) {
  const currentLocale = useLocale();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSwitch = (locale: string) => {
    if (locale === currentLocale) {
      setOpen(false);
      return;
    }
    const maxAge = 60 * 60 * 24 * 365; // 1 year
    document.cookie = `${PUBLIC_LANG_COOKIE}=${locale};path=/;max-age=${maxAge};SameSite=Lax`;
    window.location.reload();
  };

  const currentLabel = LOCALES.find((l) => l.code === currentLocale)?.label ?? 'English';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1.5 text-xs font-medium transition-colors duration-300 ${textClass ?? 'text-white/70 hover:text-white'} ${className ?? ''}`}
        aria-label="Switch language"
      >
        <Globe className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">{currentLabel}</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-40 rounded-xl border border-white/10 bg-black/90 backdrop-blur-xl py-1 shadow-xl">
          {LOCALES.map((locale) => (
            <button
              key={locale.code}
              type="button"
              onClick={() => handleSwitch(locale.code)}
              className="flex w-full items-center justify-between px-4 py-2 text-sm text-white/80 hover:bg-white/10 transition-colors"
            >
              <span>{locale.label}</span>
              {locale.code === currentLocale && <Check className="h-3.5 w-3.5 text-primary" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
